import React from 'react';
import Box from '@mui/material/Box';
import StepShell, { PrimaryBtn } from '../StepShell';
import { QuizAnswer } from '../types';
import { Theme, useMediaQuery, useTheme } from '@mui/material';
import { TagCategory } from '../../../services/panacea/types';
import { TAG_ICONS_BY_LOGICAL_ID } from '../../../constants/tag-icons';
import RestaurantIcon from '../../Icons/RestaurantIcon';

interface Props {
  state: Partial<QuizAnswer>;
  setState: React.Dispatch<React.SetStateAction<Partial<QuizAnswer>>>;
  onNext: () => void;
  onSkip?: (() => void) | null;
  onBack?: (() => void) | null;
  stepIdx: number;
  total: number;
  tags: TagCategory['tags'];
}

export default function EatingStyle({
  state,
  setState,
  onNext,
  onSkip,
  onBack,
  stepIdx,
  total,
  tags,
}: Props) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery<Theme>((t) => t.breakpoints.down('sm'));
  const selectedTagId = (state?.tagIds || [])[0];

  const selectTag = (tagId?: number) => {
    if (!tagId) {
      setState((prevState) => ({
        ...prevState,
        tagIds: [],
      }));
      return;
    }

    setState((prevState) => ({
      ...prevState,
      tagIds: [tagId],
    }));
  };

  return (
    <StepShell
      stepIdx={stepIdx}
      total={total}
      mobile={isSmallScreen}
      onBack={onBack}
      onSkip={onSkip}
      footer={<PrimaryBtn onClick={onNext}>Continue</PrimaryBtn>}
    >
      <Box sx={{ pt: isSmallScreen ? '4px' : '8px', pb: '16px' }}>
        <Box
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: isSmallScreen ? 30 : 40,
            lineHeight: 1.08,
            letterSpacing: '-0.015em',
            m: 0,
            mb: '6px',
            color: '#041C2C',
            textWrap: 'balance',
          }}
        >
          Pick an eating style
        </Box>
        <Box
          component="p"
          sx={{
            fontSize: isSmallScreen ? 15 : 17,
            color: theme.palette.text.secondary,
            m: 0,
            mb: isSmallScreen ? '18px' : '24px',
          }}
        >
          Optional. Choose one if it fits how you eat.
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {tags?.map((tag) => {
            const active = selectedTagId === tag.id;
            const Icon =
              TAG_ICONS_BY_LOGICAL_ID[tag.logicalId] ?? RestaurantIcon;
            const iconFill = active
              ? theme.palette.primary.contrastText
              : '#041C2C';
            return (
              <Box
                key={tag.id}
                component="button"
                onClick={() => selectTag(tag.id)}
                sx={{
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                  background: active ? theme.palette.primary.main : '#FFFFFF',
                  color: active
                    ? theme.palette.primary.contrastText
                    : '#041C2C',
                  border: active
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid #E5E5EE',
                  borderRadius: '14px',
                  p: '16px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '14px',
                  transition: 'all 120ms cubic-bezier(0.2, 0.8, 0.2, 1)',
                }}
              >
                <Icon fill={iconFill} height={24} width={24} />
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      fontSize: 17,
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {tag.displayName}
                  </Box>
                  <Box
                    component="span"
                    sx={{ fontSize: 13, opacity: 0.85, lineHeight: 1.4 }}
                  >
                    {tag.description}
                  </Box>
                </Box>
                {active && (
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      background: '#041C2C',
                      color: theme.palette.primary.main,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </Box>
                )}
              </Box>
            );
          })}

          {/* No preference — first-class card */}
          <Box
            component="button"
            onClick={() => selectTag(undefined)}
            sx={{
              fontFamily: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
              background: selectedTagId ? '#FFFFFF' : '#041C2C',
              color: selectedTagId ? '#041C2C' : '#F7F7FF',
              border: selectedTagId
                ? '2px dashed #C5C5D0'
                : '2px solid #041C2C',
              borderRadius: '14px',
              p: '16px 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '14px',
              mt: '4px',
              transition: 'all 120ms cubic-bezier(0.2, 0.8, 0.2, 1)',
            }}
          >
            <Box
              component="span"
              sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: 17,
                  letterSpacing: '-0.005em',
                }}
              >
                No preference
              </Box>
              <Box component="span" sx={{ fontSize: 13, opacity: 0.85 }}>
                Show me all eating styles.
              </Box>
            </Box>
            {!selectedTagId && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  width: 24,
                  height: 24,
                  borderRadius: 999,
                  background: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                ✓
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </StepShell>
  );
}
